import { h, defineComponent, ref, watch } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, column, indexView, loader, pagination } from '@/ui/components';
import { bulkDelete, list as getEntityTypeList, remove } from '@/services/api/resources/entityType';
import { emptyList } from '@/services/api';
import { EntityTypeList, EntityTypeRecord } from '@/entities/entityType';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import { bulkDeleteButton, Button } from '@/ui/components/Button';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { searchFilter } from '@/ui/components/filters/filters-horizontal';
import * as qs from '@/ui/queryState';
import { filterFormHorizontal } from '@/ui/components/Columns';
import { SortDirection } from '@/ui/types';
import { useEntityTypeCheck } from '@/ui/pages/entityType/useEntityTypeCheck';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import EntityTypeEmpty from '@/ui/pages/entityType/EntityTypeEmpty.vue';
import { BulkDeletableTable } from '@/ui/components/table/BulkDeletableTable';
import { useBulkDeletable } from '@/ui/pages/useBulkDeletable';
import { router } from '@/ui/routes';
import { isEntityPropertiesEnabled } from '@/ui/helpers/featureHelper';

export const EntityTypeIndexPage = defineComponent({
  name: 'EntityTypeIndexPage',
  setup() {
    if (!hasFeature('entityTypes')) {
      return renderResourceTeaserPage(t('common.entity-types'));
    }

    if (!hasPermission(Permissions.MANAGE_ENTITY_TYPES)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);
    const search = qs.string('search', '');

    const { page, perPage } = usePagination();
    const { sort, dir } = useSorting('createdAt', SortDirection.Descending);
    const { checkResult, performCheck } = useEntityTypeCheck();

    const list = ref<EntityTypeList>(emptyList());
    const getList = useTask(
      () => getEntityTypeList({
        page: page.value,
        perPage: perPage.value,
        search: search.value,
        sort: sort.value,
        dir: dir.value,
      }),
      entityTypeList => {
        list.value = entityTypeList;
        initialLoading.value = false;
      },
    );
    watch([page, perPage, search], getList.run);

    const { selectedResources, deleteBulkTask, onCheckedRowsUpdate, isBulkDeletionDisabled } = useBulkDeletable({
      getList,
      bulkDeleteFn: bulkDelete,
    });

    const handleEditClick = (e: MouseEvent, entityType: EntityTypeRecord) => {
      e.stopPropagation();
      e.preventDefault();
      router.push({ name: 'entityTypeEdit', params: { id: entityType.id.toString() } });
    };

    const editButton = (entityType: EntityTypeRecord) => {
      if (!isEntityPropertiesEnabled() || !entityType.authorizedTo.update) {
        return null;
      }

      return h(Button, {
        props: { type: 'is-light' },
        attrs: {
          iconRight: 'edit',
          iconPack: 'far',
        },
        on: {
          click: (e: MouseEvent) => handleEditClick(e, entityType),
        },
      });
    };

    const getEditRoute = () => {
      return isEntityPropertiesEnabled()
        ? 'entityTypeItems'
        : 'entityTypeEdit';
    };

    return function render() {
      return loader('div', performCheck, [
        checkResult.value ? indexView(
          [
            filterFormHorizontal([
              bulkDeleteButton({ resources: selectedResources, disabled: isBulkDeletionDisabled }),
              searchFilter(search),
            ]),
          ],
          [
            box([
              loader('div', getList, [
                h(BulkDeletableTable, {
                  props: {
                    editRoute: getEditRoute,
                    list: list.value.data,
                    deleteFn: remove,
                    selectedRecords: selectedResources.value,
                    loading: deleteBulkTask.state.value.pending,
                    paginationLinks: list.value.meta,
                    isRowCheckable: (row: BaseEntity) => row.authorizedTo.delete,
                    recordName: {
                      plural: t('common.entity-types'),
                      singular: t('common.entity-type'),
                    },
                  },
                  on: {
                    'update:checkedRows': onCheckedRowsUpdate,
                  },
                  scopedSlots: {
                    title: ({ row: entityType }: { row: EntityTypeRecord }) => entityType.data.title,
                    default: () => [
                      column({
                        field: 'description',
                        label: t('common.description'),
                      }, {
                        default: ({ row: group }: { row: EntityTypeRecord }) => group.data.description,
                      }),
                    ],
                    beforeDeleteButton: ({ row: entityType }: { row: EntityTypeRecord }) => editButton(entityType),
                  },
                }),
                pagination({
                  current: page.value,
                  total: list.value.meta.total,
                  perPage: perPage.value,
                }),
              ], !initialLoading.value),
            ]),
          ],
        ) : box([h(EntityTypeEmpty)]),
      ], false, 'entity-type-index');
    };
  },
});
